import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { isNotEmptyValue, logMethod } from "kwikid-toolkit";
import { Subscription } from "rxjs";
import { KwikUIButtonApiCallTimerService } from "./button-api-call.timer.service";
import { TKwikUIButtonApiCallTimer } from "./button-api-call.definitions";
import { getTimerMessage } from "./button-api-call.helper";

@Component({
  selector: "kwikui-button-api-call",
  templateUrl: "./button-api-call.component.html",
  styleUrls: ["./button-api-call.component.scss"]
})
export class KwikUIButtonApiCallComponent implements OnInit {
  @Input() apiTimer: TKwikUIButtonApiCallTimer = undefined;

  @Input() apiStatus: "default" | "loading" | "retry" | "success" | "failed" =
    "default";

  @Input() class = "tui-space_right-3 tui-space_bottom-3";

  @Input() disabled = false;

  @Input() id = "";

  @Input() label = "Button";

  @Input() shape: null | "square" | "rounded" = null;

  @Input() showLoader = false;

  @Input() size: "xs" | "s" | "m" | "l" | "xl" = "m";

  @Input() styles = "";

  @Output() onClick = new EventEmitter<any>();

  @Output() onApiTimerStop: EventEmitter<any> = new EventEmitter<any>();

  isShowApiTimerMessage = false;

  icon = "";

  iconRight = "";

  apiTimerMessages: Array<any> = [];

  private timerStopSubscription: Subscription;

  private timerValueSubscription: Subscription;

  constructor(public timerService: KwikUIButtonApiCallTimerService) {}

  ngOnInit(): void {
    this.handleApiStatus(this.apiStatus);
    this.handleApiTimer(this.apiTimer);

    this.timerStopSubscription = this.timerService.stopTimer$.subscribe(
      (reset) => {
        if (reset) {
          this.handleStopTimer();
        }
      }
    );

    this.timerValueSubscription = this.timerService.timer$.subscribe(
      (value) => {
        if (isNotEmptyValue(value)) {
          const message = getTimerMessage(
            this.apiTimer.messageWithTimerPlaceholder,
            this.apiTimer.timeFormat,
            value
          );
          this.apiTimerMessages = [
            {
              message,
              type: "info"
            }
          ];
          this.isShowApiTimerMessage = true;
        }
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasKey = (object: any, key: string): boolean => {
      return object.hasOwnProperty(key);
    };
    const verifyChange = (key: string) => {
      return hasKey(changes, key) && !changes[key].firstChange;
    };
    if (verifyChange("apiStatus")) {
      this.handleApiStatus(changes.apiStatus.currentValue);
    }
    if (verifyChange("disabled")) {
      if (this.apiStatus === "default") {
        this.disabled = changes.disabled.currentValue;
      } else if (this.apiStatus === "loading") {
        this.disabled = true;
      } else {
        this.disabled = false;
      }
    }
    if (verifyChange("apiTimer")) {
      this.apiTimer = changes.apiTimer.currentValue;
      this.handleApiTimer(this.apiTimer, true);
    }
  }

  ngOnDestroy(): void {
    this.handleStopTimer();
    this.timerService.stopTimer();

    if (this.timerStopSubscription) {
      this.timerStopSubscription.unsubscribe();
    }
    if (this.timerValueSubscription) {
      this.timerValueSubscription.unsubscribe();
    }
  }

  handleApiStatus(apiStatus: string): void {
    switch (apiStatus) {
      case "default":
        this.apiStatus = "default";
        this.icon = "";
        this.iconRight = "";
        this.disabled = this.disabled;
        this.showLoader = false;
        break;
      case "loading":
        this.apiStatus = "loading";
        this.icon = "";
        this.iconRight = "";
        this.disabled = true;
        this.showLoader = true;
        break;
      case "retry":
        this.apiStatus = "retry";
        this.icon = "tuiIconRotateCw";
        this.iconRight = "";
        this.disabled = !!isNotEmptyValue(this.apiTimer);
        this.showLoader = false;
        break;
      case "success":
        this.apiStatus = "success";
        this.icon = "";
        this.iconRight = "tuiIconCheck";
        this.disabled = false;
        this.showLoader = false;
        break;
      case "failed":
        this.apiStatus = "failed";
        this.icon = "";
        this.iconRight = "tuiIconClose";
        this.disabled = false;
        this.showLoader = false;
        break;
      default:
        break;
    }
  }

  @logMethod
  handleOnClick(e: any) {
    if (this.apiStatus === "default" || this.apiStatus === "retry") {
      this.onClick.emit({ event: e, key: this.id });
    }
  }

  @logMethod
  handleApiTimer(apiTimer, onChange = false) {
    if (isNotEmptyValue(apiTimer)) {
      this.disabled = true;
      this.timerService.startTimer(apiTimer);
    } else if (onChange) {
      this.resetApiTimer();
    }
  }

  @logMethod
  handleStopTimer() {
    this.resetApiTimer();
    this.onApiTimerStop.emit({ key: this.id });
  }

  resetApiTimer() {
    this.apiTimerMessages = [];
    this.isShowApiTimerMessage = false;
    this.disabled = false;
  }
}
